<template>
  <div class="space-y-6">
    <HermesCard title="Run">
      <div class="flex flex-wrap items-center gap-x-6 gap-y-3">
        <label class="inline-flex items-center gap-2">
          <span class="text-gray-500">Model</span>
          <select v-model="form.model" :class="SIZED_INPUT">
            <option value="">{{ bench.defaultModel || 'deployed' }} (deployed)</option>
            <option v-for="m in models.filter((x) => x !== bench.defaultModel)" :key="m" :value="m">{{ m }}</option>
          </select>
        </label>
        <label class="inline-flex items-center gap-2">
          <span class="text-gray-500">Reasoning</span>
          <select v-model="form.reasoning" :class="SIZED_INPUT">
            <option value="">{{ bench.defaultReasoning || 'deployed' }} (deployed)</option>
            <option v-for="r in ['low', 'medium', 'high'].filter((x) => x !== bench.defaultReasoning)" :key="r" :value="r">{{ r }}</option>
          </select>
        </label>
        <label class="inline-flex items-center gap-2">
          <span class="text-gray-500">Hats</span>
          <select v-model="form.hats" :class="SIZED_INPUT"><option value="">on</option><option value="off">off</option></select>
        </label>
        <div class="ml-auto flex items-center gap-4">
          <span class="text-gray-500">{{ scopeLine }}</span>
          <button type="button" :class="PRIMARY_BUTTON" :disabled="starting || !caseLabels.length" @click="start">
            {{ starting ? 'Starting…' : 'Run' }}
          </button>
        </div>
      </div>
      <p v-if="startError" class="mt-3 text-red-600">{{ startError }}</p>
    </HermesCard>

    <TestCompare v-if="picked.length === 2" :bench="bench" :a="picked[0]" :b="picked[1]" />

    <HermesCard title="Runs" :meta="runs.length ? 'tick two to compare' : ''" flush>
      <template #actions>
        <button v-if="picked.length" type="button" :class="TEXT_BUTTON" @click="picked = []">Clear selection</button>
      </template>
      <p v-if="loading && !runs.length" class="px-4 py-6 text-gray-500">Loading…</p>
      <p v-else-if="!runs.length" class="px-4 py-6 text-gray-500">No runs yet.</p>
      <table v-else class="w-full">
        <thead class="border-b border-gray-200"><tr>
          <th :class="[TH, 'w-8']" /><th :class="TH">Run</th><th :class="TH">Settings</th><th :class="TH">Status</th>
          <th :class="TH">Progress</th><th :class="[TH, 'text-right']">Judge</th><th :class="[TH, 'text-right']">Cost</th><th :class="[TH, 'text-right']">Time</th><th :class="TH" />
        </tr></thead>
        <tbody class="divide-y divide-gray-100">
          <template v-for="r in runs" :key="r.id">
            <tr class="cursor-pointer hover:bg-gray-50" :class="open === r.id ? 'bg-gray-50' : ''" @click="toggle(r.id)">
              <td :class="TD" @click.stop><input type="checkbox" :checked="picked.includes(r.id)" :disabled="!picked.includes(r.id) && picked.length >= 2" @change="pick(r.id)"></td>
              <td :class="TD" class="whitespace-nowrap">
                <span class="font-medium">#{{ r.id }}</span>
                <span class="block text-gray-500" :title="absolute(r.created_at)">{{ relative(r.created_at) }} · {{ r.requested_by_name }}</span>
              </td>
              <td :class="TD" class="text-gray-700">{{ paramsLine(r) }}</td>
              <td :class="TD"><HermesBadge :value="r.status" /></td>
              <td :class="TD" class="whitespace-nowrap">{{ progressLine(r) || '—' }}</td>
              <td :class="[TD, 'text-right tabular-nums']">{{ judge(r) }}</td>
              <td :class="[TD, 'text-right tabular-nums']">{{ money((r.usage || {}).cost_usd) }}</td>
              <td :class="[TD, 'text-right tabular-nums whitespace-nowrap']">{{ duration(r.started_at, r.finished_at) }}</td>
              <td :class="[TD, 'text-right']" @click.stop>
                <button v-if="['pending', 'running'].includes(r.status)" type="button" :class="TEXT_BUTTON" @click="cancel(r)">Cancel</button>
              </td>
            </tr>
            <tr v-if="open === r.id">
              <td colspan="9" class="px-4 py-4 bg-gray-50"><TestRunResults :bench="bench" :run-id="r.id" /></td>
            </tr>
          </template>
        </tbody>
      </table>
      <p v-if="error" class="px-4 py-3 text-red-600">{{ error }}</p>
    </HermesCard>
  </div>
</template>

<script>
import HermesBadge from '../HermesBadge.vue';
import HermesCard from '../HermesCard.vue';
import { API, INPUT, SIZED_INPUT, PRIMARY_BUTTON, TD, TEXT_BUTTON, TH, absolute, duration, errorOf, money, relative } from '../hermes';
import { casesOf, onBench, paramsLine, progressLine } from './bench';
import TestRunResults from './TestRunResults.vue';
import TestCompare from './TestCompare.vue';

export default {
  name: 'TestRuns',
  components: { HermesBadge, HermesCard, TestRunResults, TestCompare },
  props: { bench: { type: Object, required: true }, selected: { type: Array, default: () => [] } },
  data() {
    return {
      form: { model: '', reasoning: '', hats: '' }, models: ['claude-sonnet-5-5', 'gemini-3.8-flash', 'claude-opus-5-5'],
      runs: [], loading: true, error: '', starting: false, startError: '', timer: null, open: null, picked: [],
      INPUT, PRIMARY_BUTTON, SIZED_INPUT, TD, TEXT_BUTTON, TH,
    };
  },
  computed: {
    caseLabels() {
      return casesOf(this.bench.template).map((c) => c.label);
    },
    scopeLine() {
      const n = this.selected.length || this.caseLabels.length;
      const which = this.selected.length ? `${n} selected case${n === 1 ? '' : 's'}` : `all ${n} case${n === 1 ? '' : 's'}`;
      return this.estimate ? `${which} · ${this.estimate}` : which;
    },
    estimate() {
      const done = this.runs.find((r) => r.status === 'completed' && (r.progress || {}).done && (r.usage || {}).cost_usd);
      if (!done) return '';
      const per = done.usage.cost_usd / done.progress.done;
      const n = this.selected.length || this.caseLabels.length;
      return `about ${money(per * n)}`;
    },
  },
  mounted() {
    this.load();
  },
  beforeUnmount() {
    clearTimeout(this.timer);
  },
  methods: {
    absolute, duration, money, relative, paramsLine, progressLine,
    judge(r) {
      const j = (r.metadata || {}).judge_avg;
      return j === null || j === undefined ? '—' : `${j} / 5`;
    },
    toggle(id) {
      this.open = this.open === id ? null : id;
    },
    pick(id) {
      this.picked = this.picked.includes(id) ? this.picked.filter((x) => x !== id) : [...this.picked, id].slice(-2);
    },
    async load() {
      clearTimeout(this.timer);
      this.error = '';
      try {
        const { data } = await this.$axios.get(`${API}/runs/`, onBench(this.bench, { params: { template: this.bench.template.id, limit: 50 } }));
        this.runs = data.results;
        if (this.runs.some((r) => ['pending', 'running'].includes(r.status))) this.timer = setTimeout(this.load, 5000);
      } catch (e) {
        this.error = errorOf(e, 'Runs could not be loaded.');
      } finally {
        this.loading = false;
      }
    },
    async start() {
      this.starting = true;
      this.startError = '';
      const params = {};
      if (this.form.model.trim()) params.model = this.form.model.trim();
      if (this.form.reasoning) params.reasoning = this.form.reasoning;
      if (this.form.hats) params.hats = this.form.hats;
      if (this.selected.length) params.only = this.selected.join(',');
      try {
        const { data } = await this.$axios.post(`${API}/runs/start/`, { executor: this.bench.executor.key, template: this.bench.template.id, params }, onBench(this.bench));
        this.open = data.id;
        await this.load();
      } catch (e) {
        this.startError = errorOf(e, 'The run was not started.');
      } finally {
        this.starting = false;
      }
    },
    async cancel(r) {
      try {
        await this.$axios.post(`${API}/runs/${r.id}/cancel/`, null, onBench(this.bench));
        this.load();
      } catch (e) {
        this.error = errorOf(e, 'The run could not be cancelled.');
      }
    },
  },
};
</script>
